/**
 * Infection — the slow killer (AoD §5.4).
 *
 * The other three clocks are decided in a minute; this one takes a week. A wolf
 * gets its teeth into a farmer's forearm, he limps home, and the fight is over.
 * Then, over the following days, the arm goes red, then hot, then he is
 * shivering by the fire and useless, and then somebody has to decide whether to
 * take the arm off. *That* is what makes a survivable mauling frightening, and
 * it is why decision log #7 put this in the MVP rather than in the medicine
 * milestone where it would have been more comfortable.
 *
 * Two design commitments:
 *
 * 1. **The risk is already authored.** Every wound-table cell carries an
 *    `infectionMod` scaled by damage type, level and zone — a bite is worse
 *    than a spear because `bite.ts` says so, a gut wound is worse than a
 *    forearm because `puncture.ts` says so. This module multiplies that by a
 *    single rate and by what care and weather have to say. It does *not*
 *    re-derive per-type or per-level factors, because doing so would count the
 *    same judgement twice and put balance in two files.
 * 2. **The stage is the clock.** `wound.infectionClock` was reserved in M1-02;
 *    1/2/3 are the track and 4 is the end of it. No parallel state field, so a
 *    wound that survives a save round-trip carries its illness with it, and a
 *    replay stepping the clock twice cannot invent a stage.
 *
 * What the player sees is symptoms. The true stage is hidden until somebody
 * with medicine looks (M4); until then, narration hints.
 */
import { TICKS_PER_GAME_DAY } from './time.js';
import { die } from './vitals.js';
/* -------------------------------------------------------------------------- *
 * The track
 * -------------------------------------------------------------------------- */
/**
 * The progression (AoD §5.4). Which terminal stage a wound reaches is decided
 * by its zone, not by a roll: a limb rots (`gangrenous`) and offers the era's
 * terrible bargain; a torso or a head goes `septic` and offers nothing.
 */
export const INFECTION_STAGES = ['inflamed', 'fevered', 'gangrenous', 'septic'];
/** Clock value at which the track has run out and the character dies. */
export const INFECTION_TERMINAL_CLOCK = 4;
/** Zones an infection can be cut out of. Everything else is sepsis. */
export const LIMB_ZONES = ['arm', 'hand', 'leg', 'foot'];
export function isLimbZone(zone) {
    return LIMB_ZONES.includes(zone);
}
/**
 * The stage a wound is at, or `null` if it is not infected. Derived from the
 * clock so that there is exactly one place illness is written down.
 */
export function infectionStageOf(wound) {
    if (wound.state !== 'infected')
        return null;
    if (wound.infectionClock <= 0)
        return null;
    if (wound.infectionClock === 1)
        return 'inflamed';
    if (wound.infectionClock === 2)
        return 'fevered';
    return isLimbZone(wound.zone) ? 'gangrenous' : 'septic';
}
/**
 * What a bystander can see. The true stage is hidden state — a fevered man and
 * a gangrenous one both look like a fevered man until somebody unwraps the
 * dressing and knows what they are looking at (M4's medicine reveal).
 */
export const INFECTION_SYMPTOMS = {
    inflamed: 'the wound is red and hot',
    fevered: 'flushed and shivering',
    gangrenous: 'flushed and shivering',
    septic: 'flushed and shivering',
};
export function observedSymptom(wound) {
    const stage = infectionStageOf(wound);
    return stage === null ? null : INFECTION_SYMPTOMS[stage];
}
/**
 * How ill each stage makes the whole body, in [0, 1] — `Vitals.fever`, which
 * cuts pools and stops blood coming back.
 *
 * `inflamed` is 0 on purpose: a red wound is a local problem and a local pain
 * rise, not a systemic one. The character is not ill yet, which is exactly the
 * window in which cleaning it still costs nothing.
 */
export const INFECTION_FEVER = {
    inflamed: 0,
    fevered: 0.6,
    gangrenous: 1,
    septic: 1,
};
/** Pain a wound gains the day it turns. Local, permanent until it is treated. */
export const INFLAMED_PAIN_RISE = 2;
/* -------------------------------------------------------------------------- *
 * Risk
 * -------------------------------------------------------------------------- */
/** Below this level a wound is a scratch and does not roll (AoD §5.4). */
export const INFECTION_MIN_LEVEL = 2;
/**
 * The one rate that turns an authored `infectionMod` into a per-game-hour
 * chance. **This is the balance knob for the whole system**: raise it and every
 * bite in the game gets more dangerous in proportion, which is the change a
 * tuning pass actually wants to make.
 *
 * Calibrated against M1-05's acceptance envelope: an untreated level-2 bite to
 * the arm carries `infectionMod` 0.25, so `0.25 × 0.115 ≈ 2.9%` per hour, which
 * is a hair over even odds across a game day.
 */
export const INFECTION_HOURLY_RATE = 0.115;
/**
 * What treatment is worth (AoD §5.4).
 *
 * - `fresh` — open and dirty. Full risk.
 * - `treated` — washed out and dressed. An eighth of the risk, not none: the
 *   Meddyg improves your odds, she does not sell immunity.
 * - `healing` — closed over. Nothing left for dirt to get into.
 * - `infected` — already lost this roll; the daily track has it now.
 * - `healed` / `permanent` — done with, one way or the other.
 */
export const INFECTION_STATE_FACTOR = {
    fresh: 1,
    treated: 0.12,
    healing: 0,
    infected: 0,
    healed: 0,
    permanent: 0,
};
export const CLEAN_CONDITIONS = { filth: 1 };
export const FOUL_CONDITIONS = { filth: 2 };
/**
 * How much a lapsed dressing gives back of the risk a fresh one bought off
 * (M4-02's dressing-change rhythm).
 *
 * A dressing wants renewing each game day; left on longer it sours, and the
 * wound under it starts rolling again — at `treated × this`, so worse than a
 * maintained dressing but still short of an open wound. That gap is the whole
 * reason to change a dressing, and the reason an infirmary bed that gets changed
 * for you is worth lying in (M4-08). Multiplied into the `treated` factor, so a
 * lapsed dressing at 0.12 × 3 = 0.36 is three times the risk of a fresh one and
 * still a third of an untreated wound's.
 */
export const LAPSED_DRESSING_FACTOR = 3;
/** A dressing left on longer than this (game ticks) has lapsed. */
export const DRESSING_LIFE = TICKS_PER_GAME_DAY;
/** Has the dressing on a treated wound gone past its day, at this tick? */
export function dressingLapsed(wound, tick) {
    return wound.state === 'treated' && wound.dressedAt > 0 && tick - wound.dressedAt > DRESSING_LIFE;
}
/**
 * Chance, per game hour, that this wound turns. 0 if it is not at risk.
 *
 * `tick` is optional: without it — the combat harness, and every M1-05 test that
 * predates dressings — a treated wound simply rolls at its treated rate. With
 * it, a treated wound whose dressing has lapsed rolls higher (see
 * `LAPSED_DRESSING_FACTOR`), which is what makes daily wound care a thing worth
 * doing rather than a one-time payment.
 */
export function infectionChance(wound, environment = CLEAN_CONDITIONS, tick) {
    if (wound.level < INFECTION_MIN_LEVEL)
        return 0;
    let care = INFECTION_STATE_FACTOR[wound.state];
    if (care <= 0)
        return 0;
    if (tick !== undefined && dressingLapsed(wound, tick))
        care *= LAPSED_DRESSING_FACTOR;
    const chance = wound.infectionMod * INFECTION_HOURLY_RATE * care * environment.filth;
    return chance <= 0 ? 0 : Math.min(1, chance);
}
/* -------------------------------------------------------------------------- *
 * The hourly roll
 * -------------------------------------------------------------------------- */
/**
 * The `hour` phase: every open wound rolls to turn.
 *
 * Wounds are walked in list order and a wound that cannot turn never touches
 * the dice, so the stream position after a call depends only on how many wounds
 * were actually at risk — which is state, and therefore replayable.
 */
export function rollInfections(rng, vitals, environment = CLEAN_CONDITIONS, tick) {
    if (vitals.dead)
        return [];
    const events = [];
    for (const wound of vitals.wounds) {
        const chance = infectionChance(wound, environment, tick);
        if (chance <= 0)
            continue;
        if (!rng.chance(chance))
            continue;
        wound.state = 'infected';
        wound.infectionClock = 1;
        wound.pain += INFLAMED_PAIN_RISE;
        events.push({ type: 'infected', refs: [wound.id, wound.part, 'inflamed'] });
    }
    if (events.length > 0)
        recomputeFever(vitals);
    return events;
}
/* -------------------------------------------------------------------------- *
 * The daily track
 * -------------------------------------------------------------------------- */
/** Sides on the constitution die the daily progression is checked against. */
export const INFECTION_DIE = 20;
/**
 * The `day` phase: each infected wound is checked against toughness, and the
 * track moves.
 *
 * A failed roll advances it. A *comfortably* made roll — half the target or
 * better — walks it back, and a wound walked back to 0 is one the body beat.
 * Without that second door the track is a countdown with no way off it, and an
 * infection nobody could ever survive is not a drama, it is a delayed death
 * sentence.
 *
 * `care` is the modifier every treatment in M4 adds: the Meddyg's dressings,
 * yarrow, a clean hut, somebody to sit with them.
 */
export function advanceInfection(rng, vitals, attributes, care = 0) {
    if (vitals.dead)
        return [];
    const events = [];
    const target = attributes.toughness + care;
    for (const wound of vitals.wounds) {
        if (wound.state !== 'infected')
            continue;
        const roll = rng.roll(INFECTION_DIE);
        if (roll > target) {
            wound.infectionClock += 1;
            if (wound.infectionClock >= INFECTION_TERMINAL_CLOCK) {
                events.push({ type: 'infection-stage', refs: [wound.id, wound.part, 'terminal'] });
                events.push(...die(vitals, 'infection'));
                recomputeFever(vitals);
                return events;
            }
            const stage = infectionStageOf(wound);
            // Gangrene is what `BodyPartState.destroyed` was reserved for in M1-01:
            // a limb that is dead where it hangs, which no healing gives back.
            if (stage === 'gangrenous') {
                const state = vitals.body.parts[wound.part];
                if (state)
                    state.destroyed = true;
            }
            events.push({ type: 'infection-stage', refs: [wound.id, wound.part, stage] });
            continue;
        }
        if (roll * 2 <= target) {
            wound.infectionClock -= 1;
            if (wound.infectionClock <= 0) {
                wound.infectionClock = 0;
                wound.state = 'treated';
                events.push({ type: 'infection-halted', refs: [wound.id, wound.part, 'fought-off'] });
                continue;
            }
            events.push({
                type: 'infection-stage',
                refs: [wound.id, wound.part, infectionStageOf(wound)],
            });
        }
    }
    recomputeFever(vitals);
    return events;
}
/**
 * Recompute systemic illness from the wound list — the worst infected wound
 * decides, and nothing sums.
 *
 * Same discipline as impairment, for the same reason: three inflamed scratches
 * must not add up to a fever, and recomputation makes replaying a day
 * idempotent.
 */
export function recomputeFever(vitals) {
    let worst = 0;
    for (const wound of vitals.wounds) {
        const stage = infectionStageOf(wound);
        if (stage === null)
            continue;
        const fever = INFECTION_FEVER[stage];
        if (fever > worst)
            worst = fever;
    }
    vitals.fever = worst;
}
/* -------------------------------------------------------------------------- *
 * The terrible bargain
 * -------------------------------------------------------------------------- */
/**
 * Chance that taking the limb off stops the track. High, as AoD asks, and not
 * certain — the era's bargain is a good bet, not a cure.
 */
export const AMPUTATION_HALT_CHANCE = 0.75;
/**
 * Take a part off to stop what is in it.
 *
 * The mechanic lives here rather than with the surgery it belongs to (M4-05)
 * because M1-05 has to be able to *assert* that gangrene is stoppable — an
 * infection track with no exit is not the mechanic this ticket describes. M4-05
 * wraps this in skill, tools, shock and the patient's opinion; the arithmetic
 * of what amputation does to the body does not change when it does.
 *
 * Wounds on the part become `permanent` — the loss is not going to heal, and a
 * stump is not a thing dirt gets into any more. If the roll fails, the worst
 * one stays `infected` and keeps its clock, and the track goes on without a
 * limb left to trade.
 */
export function amputate(rng, vitals, part) {
    const state = vitals.body.parts[part];
    if (!state)
        throw new RangeError(`body has no part '${part}'`);
    if (vitals.dead)
        return [];
    const onPart = vitals.wounds.filter((wound) => wound.part === part);
    const halted = rng.chance(AMPUTATION_HALT_CHANCE);
    state.severed = true;
    let worst = null;
    for (const wound of onPart) {
        if (wound.state !== 'infected')
            continue;
        if (worst === null || wound.infectionClock > worst.infectionClock)
            worst = wound;
    }
    for (const wound of onPart) {
        if (halted || wound !== worst) {
            wound.state = 'permanent';
            wound.infectionClock = 0;
        }
        // The cut itself bleeds; the wound it was cutting out does not, any more.
        wound.bleedRate = 0;
    }
    recomputeFever(vitals);
    const events = [
        { type: 'amputated', refs: [part, halted ? 'halted' : 'too-late'] },
    ];
    if (halted && worst !== null) {
        events.push({ type: 'infection-halted', refs: [worst.id, part, 'amputation'] });
    }
    return events;
}
//# sourceMappingURL=infection.js.map